import { matchPath, useLocation } from 'react-router-dom';
import { useAppSelector } from 'src/app/store/stateTypeHook';
// material ui components
import { List, ListSubheader } from '@mui/material';
import { styled } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
// sidebar components
import SidebarMenuItem from './item';
import menuItems, { MenuItem } from './items';

const MenuWrapper = styled(List)(
    ({ theme }) => `
      margin-bottom: ${theme.spacing(2.25)};
      padding: 0 22px;
      @media (min-width: ${theme.breakpoints.values.lg}px) {
        padding: 0 15px;
      }

      & > .MuiList-root {
        padding: 0 ${theme.spacing(2)} ${theme.spacing(2)};
      }
  `
);

const SubMenuWrapper = styled(List)(
    ({ theme }) => `
      &.MuiList-root {
        padding: 0;
        .MuiList-root .MuiList-root .MuiListItem-root .MuiButton-root {
          font-weight: normal !important;
        }
  
        .MuiListItem-root {
          padding: 0;
      
          .MuiButton-root {
            display: flex;
            color: ${theme.palette.alpha.primary[40]};
            justify-content: flex-start;
            font-size: ${theme.typography.pxToRem(14)};
            position: relative;
  
            .MuiBadge-root {
              position: absolute;
              right: ${theme.spacing(4)};
  
              .MuiBadge-standard {
                background: ${theme.palette.primary.main};
                font-size: ${theme.typography.pxToRem(9)};
                font-weight: bold;
                color: ${theme.palette.error.main};
              }
            }
      
            .MuiButton-startIcon,
            .MuiButton-endIcon {
              transition: ${theme.transitions.create(['color'])};
  
              .MuiSvgIcon-root {
                font-size: inherit;
                transition: none;
              }
            }
  
            .MuiButton-startIcon {
              font-size: ${theme.typography.pxToRem(26)};
              margin-right: ${theme.spacing(1.5)};
              color: ${theme.palette.alpha.primary[30]};
            }
            
            .MuiButton-endIcon {
              margin-left: auto;
              font-size: ${theme.typography.pxToRem(22)};
            }
  
            &.active,
            &:hover {
              color: ${theme.palette.primary.main};
              
              
              .MuiButton-startIcon,
              .MuiButton-endIcon {
                  color: ${theme.palette.primary.light};
              }
            }
          }
  
          &.Mui-children {
            flex-direction: column;
            line-height: 1;
            padding: 18px 8px;
            border-radius:15px;
          }
          &.Mui-children.active {
            background: ${theme.palette.alpha.info[5]};
          }
  
          .MuiCollapse-root {
            width: 100%;
  
            .MuiList-root {
              padding: ${theme.spacing(0, 0)};
            }
  
            .MuiListItem-root {
              padding: ${theme.spacing(0)};
  
              .MuiButton-root {
                font-size: ${theme.typography.pxToRem(14)};
  
                &.active,
                &:hover {
                  color: ${theme.palette.primary.main};
                  background-color: transparent;
                }
              }
            }
          }
        }
      }
  `
);

const renderSidebarMenuItems = ({ items, path }: { items: MenuItem[]; path: string }): JSX.Element => (
    <SubMenuWrapper>{items.reduce((ev, item) => reduceChildRoutes({ ev, item, path }), [])}</SubMenuWrapper>
);

const reduceChildRoutes = ({
    ev,
    path,
    item,
}: {
    ev: JSX.Element[];
    path: string;
    item: MenuItem;
}): Array<JSX.Element> => {
    const key = item.name;

    const exactMatch = item.link
        ? !!matchPath(
              {
                  path: item.link,
                  end: true,
              },
              path
          )
        : false;

    const partialMatch = item.link
        ? !!matchPath(
              {
                  path: item.link,
                  end: false,
              },
              path
          )
        : false;

    if (item.items) {
        ev.push(
            <SidebarMenuItem
                key={key}
                active={partialMatch}
                open={partialMatch}
                name={item.name}
                icon={item.icon}
                activeIcon={item.activeIcon}
                link={item.link}
                badge={item.badge}
                isMain={item.isMain}
            >
                {renderSidebarMenuItems({
                    path,
                    items: item.items,
                })}
            </SidebarMenuItem>
        );
    } else {
        ev.push(
            <SidebarMenuItem
                key={key}
                active={partialMatch}
                name={item.name}
                link={item.link}
                badge={item.badge}
                icon={item.icon}
                activeIcon={item.activeIcon}
                isMain={item.isMain}
            />
        );
    }

    return ev;
};

function SidebarMenu() {
    const location = useLocation();
    const { loggedinUser } = useAppSelector((state) => state.user);
    const userLoaded = loggedinUser && loggedinUser.systemPermissions;
    let grantedMenu = [...menuItems];
    if (userLoaded) {
        // filter the granted menu
        grantedMenu.forEach((i) => {
            i.items.forEach((j, index) => {
                const page = j.items.filter((k) => loggedinUser.systemPermissions.indexOf(k.secret) !== -1);
                j.items = page;
            });

            // remove the empty submenu
            grantedMenu.forEach((e, i) => {
                e.items = e.items.filter((j) => j.items.length > 0);
            });
        });
    }

    return (
        <>
            {userLoaded ? (
                grantedMenu.map((section) => (
                    <MenuWrapper
                        key={section.heading}
                        subheader={
                            <ListSubheader component='div' disableSticky>
                                {/* {section.heading} */}
                            </ListSubheader>
                        }
                    >
                        {renderSidebarMenuItems({
                            items: section.items,
                            path: location.pathname,
                        })}
                    </MenuWrapper>
                ))
            ) : (
                <>
                    <Skeleton
                        sx={{ mx: '15px', mb: '18px', borderRadius: '15px' }}
                        variant='rectangular'
                        width={144}
                        height={195}
                    />
                    <Skeleton
                        sx={{ mx: '15px', borderRadius: '15px' }}
                        variant='rectangular'
                        width={144}
                        height={195}
                    />
                </>
            )}
        </>
    );
}

export default SidebarMenu;
